import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  ICohortRow,
  IRowMeta,
  populateNamedBuilderCohort,
  populateNamedCohortReference,
  removeCohortRow,
  RowType,
} from '../../../../redux/slices/oldBuilder';
import { AliasHighlight } from '../../AliasNavigation/AliasHighlight';
import {
  HighlightedEntity,
  useHighlightingContext,
  withHighlightingContext,
} from '../../../HighlightingContext';
import { isValidCohortRow } from '../../../../utils/builder/cohorts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { SaveCohortModal } from '../../../Cohort';
import {
  FunnelModeKey,
  ICohortFieldsFragment,
  PermissionKey,
  SimpleCohortTypeKey,
} from '../../../../utils/apollo/graphqlSchema';
import CohortImportToBrazeModal from '../../../Cohort/CohortImportToBrazeModal';
import SimpleCohort from './SimpleCohort';
import BuilderCohortFilters from './BuilderCohortFilters';
import usePermission from '../../../../utils/hooks/permissionHooks';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import CohortRowHeader from './CohortRowHeader';
import { BuilderRowActions, RowActionsMenu } from '../../BuilderActions';
import { IStyled } from '../../../../styles/types';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../../ActionsVisibilityContext';
import { ITooltipOption } from '../../../KubitTooltip/globalTooltips';
import { activeSchemaIdSelector } from '../../../../redux/selectors/builder';
import DndDroppableZone from '../../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../../QuickAccessDictionary/DictionaryTabs/types';
import { useDndEntityInternalContext } from '../../../DndEntity';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../DndEntity/utils';
import { isEmptyEventConditions } from '../../../../utils/builder';
import {
  EntityDropzoneDataTypeEnum,
  ICohortEntityDropzoneDataType,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../../DndEntity/validation';
import { MAX_EVENT_CONDITIONS_COUNT } from '../../../../config/constants';
import { CohortRowContext, ICohortRowContext } from './CohortRowContext';
import { borderedContent } from '../../../../styles/shareableStyles';
import { SingleSelectOption } from '../../../Dropdown/SingleSelect';
import { SquareRemoveButton } from '../../../Button';
import useAnalysisBuilderInfo from '../../../../redux/selectorHooks/useAnalysisBuilderInfo';

interface IProps extends IStyled {
  rowMeta: IRowMeta;
  cohortRow: ICohortRow;
  allowRemove: boolean;
  removeActionTooltip?: ITooltipOption;
}

const Container = styled(HighlightedEntity)`
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 12px 16px;
  ${borderedContent};
`;

const defaultRemoveActionTooltip: ITooltipOption = {
  content: 'Remove Cohort',
};

const CohortRow = ({
  rowMeta,
  cohortRow,
  className,
  allowRemove,
  removeActionTooltip,
}: IProps) => {
  const dispatch = useAppDispatch();

  const schemaId = useAppSelector(activeSchemaIdSelector);
  const { checkIsPermitted } = usePermission();
  const isPermittedToDisplayCreateCohortButton = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CohortCreate,
      PermissionKey.CohortEdit,
    ],
    validateKind: 'OR',
    schemaId,
  });

  const { activeItem } = useDndEntityInternalContext();

  const {
    cohortId,
    userAliasId,
    simpleCohortTypeKey,
    eventConditions,
    isExpanded,
    isCompoundCohort,
    analysisId,
  } = cohortRow;
  const { rowType, rowIndex } = rowMeta;

  const builderInfo = useAnalysisBuilderInfo(analysisId);
  const saveCohortModalId = rowType + '-' + rowIndex;

  const isValidCohortInput = isValidCohortRow(cohortRow);

  const isSaveCohortModalOpen = useAppSelector((state) =>
    selectOpenedModalType(
      state,
      ModalSliceType.SaveCohortRow,
      saveCohortModalId,
    ),
  );

  const {
    activateHighlighting,
    deactivateHighlighting,
    clearHighlightedByTimeout,
  } = useHighlightingContext();

  const { actionsVisible, showActions, hideActions } =
    useActionsVisibilityContext();

  const onSaveButtonClick = () => {
    // validation is done at this moment, otherwise the save button will not be displayed
    dispatch(
      openModal({
        type: ModalSliceType.SaveCohortRow,
        data: {
          rowMeta,
        },
        id: saveCohortModalId,
      }),
    );

    deactivateHighlighting();
    hideActions();
  };

  const onRemoveButtonClick = () => {
    dispatch(removeCohortRow(rowMeta));

    deactivateHighlighting();
  };

  const handleSaveSimpleCohort = (cohort: ICohortFieldsFragment) => {
    activateHighlighting();

    clearHighlightedByTimeout();

    if (rowType === RowType.builderCohorts) {
      dispatch(
        populateNamedBuilderCohort({
          cohort,
          rowMeta,
        }),
      );

      return;
    }

    // Populate cohort as reference
    dispatch(
      populateNamedCohortReference({
        cohort,
        rowMeta,
      }),
    );
  };

  const allowSave = isValidCohortInput && !cohortId;

  const displayCohortData = Boolean(!cohortId || isExpanded);

  const showCohortFilters = useMemo(() => {
    if (!displayCohortData) {
      return false;
    }

    if (
      simpleCohortTypeKey === SimpleCohortTypeKey.Expression ||
      simpleCohortTypeKey === SimpleCohortTypeKey.Funnel ||
      simpleCohortTypeKey === SimpleCohortTypeKey.Retention
    ) {
      return true;
    }

    return eventConditions.length > 1;
  }, [displayCohortData, eventConditions, simpleCohortTypeKey]);

  const onMouseEnter = () => {
    showActions();
    if (!displayCohortData) {
      activateHighlighting();
    }
  };

  const onMouseLeave = () => {
    hideActions();
    if (!displayCohortData) {
      deactivateHighlighting();
    }
  };

  const dropzoneId = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (
      data &&
      (data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent')
    ) {
      return createDictionaryEntityDropzoneId(
        EntityDropzoneType.Events,
        rowMeta,
      );
    }

    if (
      data &&
      (data.__typename === 'TagLeafSimpleMeasure' ||
        data.__typename === 'TagLeafCompoundMeasure')
    ) {
      return createDictionaryEntityDropzoneId(
        EntityDropzoneType.Measures,
        rowMeta,
      );
    }

    return createDictionaryEntityDropzoneId(
      EntityDropzoneType.Cohorts,
      rowMeta,
    );
  }, [activeItem, rowMeta]);

  const dropzoneData = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (data) {
      if (
        data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent'
      ) {
        const areEventConditionsEmpty = isEmptyEventConditions(eventConditions);

        return {
          id: dropzoneId,
          rowMeta: {
            ...rowMeta,
            eventIndex: areEventConditionsEmpty ? 0 : eventConditions.length,
          },
          isNewEvent: !areEventConditionsEmpty,
          shouldAddMeasureFunction: areEventConditionsEmpty,
          type: EntityDropzoneDataTypeEnum.Events,
        } satisfies IEventEntityDropzoneDataType;
      }

      if (
        data.__typename === 'TagLeafSimpleMeasure' ||
        data.__typename === 'TagLeafCompoundMeasure'
      ) {
        return {
          id: dropzoneId,
          type: EntityDropzoneDataTypeEnum.Measures,
          rowMeta,
        } satisfies IMeasureEntityDropzoneDataType;
      }

      if (
        data.__typename === 'TagLeafSimpleCohort' ||
        data.__typename === 'TagLeafCompoundCohort'
      ) {
        return {
          id: dropzoneId,
          type: EntityDropzoneDataTypeEnum.Cohorts,
          rowMeta,
        } satisfies ICohortEntityDropzoneDataType;
      }
    }
  }, [activeItem, rowMeta, eventConditions, dropzoneId]);

  const cohortContextValue = useMemo<ICohortRowContext>(
    () => ({
      rowMeta,
      cohortRow,
      // check if selected funnel analysis is time to convert mode
      isTimeToConvertRow:
        simpleCohortTypeKey === SimpleCohortTypeKey.Funnel &&
        builderInfo?.query?.__typename === 'FunnelQuery' &&
        builderInfo?.query?.mode?.key === FunnelModeKey.TimeToConvert,
    }),
    [cohortRow, rowMeta, simpleCohortTypeKey, builderInfo],
  );

  const getCustomMessage = () => {
    const data = getDndActiveItemData(activeItem);
    if (data) {
      if (rowType === RowType.breakdownCohorts) {
        if (
          data.__typename === 'TagLeafSimpleMeasure' ||
          data.__typename === 'TagLeafCompoundMeasure'
        ) {
          return 'Replace Segment with New Measure';
        }

        if (
          data.__typename === 'TagLeafSimpleCohort' ||
          data.__typename === 'TagLeafCompoundCohort'
        ) {
          return 'Replace Segment with New Cohort';
        }
      }

      if (rowMeta.cohortRowType === 'cohortFilter') {
        if (
          data.__typename === 'TagLeafBasicEvent' ||
          data.__typename === 'TagLeafVirtualEvent'
        ) {
          return `+ Add Event to Filter`;
        }
        if (
          data.__typename === 'TagLeafSimpleMeasure' ||
          data.__typename === 'TagLeafCompoundMeasure'
        ) {
          return 'Replace Filter with New Measure Cohort';
        }
        return 'Replace Filter with New Cohort';
      }

      if (rowType === RowType.globalCohorts) {
        if (
          data.__typename === 'TagLeafBasicEvent' ||
          data.__typename === 'TagLeafVirtualEvent'
        ) {
          return `+ Add Event to Report Filter`;
        }
        return 'Replace Report Filter with New Cohort';
      }

      if (rowType === RowType.builderCohorts) {
        if (
          data.__typename === 'TagLeafCompoundCohort' &&
          !isEmptyEventConditions(cohortRow.eventConditions)
        ) {
          return 'Cannot Replace Cohort Row with Compound Cohort';
        }
      }

      if (
        data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent'
      ) {
        return '+ Add Event to Segment';
      }
    }
  };

  const checkHasError = () => {
    const data = getDndActiveItemData(activeItem);
    const isTagLeafEvent =
      data?.__typename === 'TagLeafBasicEvent' ||
      data?.__typename === 'TagLeafVirtualEvent';

    if (isTagLeafEvent) {
      return eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT;
    }

    if (
      data?.__typename === 'TagLeafCompoundCohort' &&
      rowMeta.rowType === RowType.builderCohorts
    ) {
      if (
        rowIndex === 0 &&
        !isEmptyEventConditions(cohortRow.eventConditions)
      ) {
        return true;
      }
      if (rowIndex > 0) {
        return true;
      }
    }

    return false;
  };

  const checkIsDisabled = () => {
    const data = getDndActiveItemData(activeItem);
    return (
      data?.__typename &&
      (data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent') &&
      simpleCohortTypeKey !== SimpleCohortTypeKey.Performed
    );
  };

  return (
    <DndDroppableZone
      accepts={[
        EntityDropzoneType.Events,
        EntityDropzoneType.Cohorts,
        EntityDropzoneType.Measures,
      ]}
      id={dropzoneId}
      data={dropzoneData}
      customMessage={getCustomMessage()}
      hasError={checkHasError()}
      isDisabled={checkIsDisabled()}
    >
      <AliasHighlight aliasId={userAliasId}>
        <CohortRowContext.Provider value={cohortContextValue}>
          <Container
            className={className}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <CohortRowHeader
              cohortRow={cohortRow}
              rowMeta={rowMeta}
              displayCohortData={displayCohortData}
              onDeactivateHighlight={deactivateHighlighting}
            >
              <BuilderRowActions>
                {actionsVisible && allowSave && (
                  <RowActionsMenu>
                    <PermissionElementWrapper
                      isPermitted={isPermittedToDisplayCreateCohortButton}
                    >
                      <SingleSelectOption
                        label={'Save Cohort'}
                        onClick={onSaveButtonClick}
                        disabled={!isPermittedToDisplayCreateCohortButton}
                        onMouseEnter={activateHighlighting}
                        onMouseLeave={deactivateHighlighting}
                      />
                    </PermissionElementWrapper>
                  </RowActionsMenu>
                )}
                {actionsVisible && allowRemove && (
                  <SquareRemoveButton
                    onClick={onRemoveButtonClick}
                    onMouseEnter={activateHighlighting}
                    onMouseLeave={deactivateHighlighting}
                    {...(removeActionTooltip || defaultRemoveActionTooltip)}
                  />
                )}
              </BuilderRowActions>
            </CohortRowHeader>
            {displayCohortData && !isCompoundCohort && <SimpleCohort />}
            {showCohortFilters && <BuilderCohortFilters rowMeta={rowMeta} />}
          </Container>
        </CohortRowContext.Provider>
        {isSaveCohortModalOpen && (
          <SaveCohortModal onSubmit={handleSaveSimpleCohort} />
        )}
        <CohortImportToBrazeModal />
      </AliasHighlight>
    </DndDroppableZone>
  );
};

export default withHighlightingContext(withActionsVisibilityContext(CohortRow));
